import React, { useCallback, useMemo, useState } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import { Button, Menu, MenuItem, styled, Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { SHOW_LESS, SHOW_MORE } from 'common/constants/pageFilters';
import { Currency } from 'common/types/format';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';
import { useLayoutContext } from 'context/LayoutContext';
import { CurrencyFormatterProps } from './GridFormatter.props';


const StyledFormatterButton = styled(Button)<{ foreignCurrency?: boolean; isExportMode?: boolean }>(({
  theme,
  foreignCurrency,
  isExportMode,
}) => {
  const primaryGreen = (theme as any).palette?.green?.primary || '#4caf50';
  const accentColor = isExportMode ? '#000' : primaryGreen;

  return {
    padding: '0 14px 0 8px',
    minWidth: 'auto',
    fontSize: '12px',
    borderRadius: '0',
    borderBottomRightRadius: '10px',
    lineHeight: 'inherit',
    backgroundColor: '#fff',
    fontWeight: 800,
    maxHeight: '19px',
    '&:hover': {
      backgroundColor: foreignCurrency ? accentColor : '#fff',
    },
  };
});

const StyledCurrencyButton = styled(StyledFormatterButton)<{ foreignCurrency?: boolean; isExportMode?: boolean }>(({
  theme,
  foreignCurrency,
  isExportMode,
}) => {
  const primaryGreen = (theme as any).palette?.green?.primary || '#4caf50';
  const accentColor = isExportMode ? '#000' : primaryGreen;

  return {
    backgroundColor: foreignCurrency ? accentColor : '#fff',
    color: foreignCurrency ? '#fff' : accentColor,
    border: `1px solid ${accentColor}`,
    zIndex: 1,
  };
});

const StyledNumberButton = styled(StyledFormatterButton)({
  color: '#b7b7b7',
  border: '1px solid #b7b7b7',
  borderLeft: 'none',
  paddingLeft: '16px !important',
  left: '-8px',
});

const StyledIcon = styled(ArrowRightAlt)({
  padding: '0 2px',
  display: 'block',
  width: '20px !important',
  height: 'auto !important',
  fill: '#fff !important',
});

const StyledMenuSeparator = styled(MenuItem)({
  borderBottom: '1px solid #EFEFEF',
});

const StyledShowMoreLess = styled(MenuItem)(({ theme }) => ({
  fontSize: '0.875rem',
  color: (theme as any).palette?.grey?.[400] || '#9e9e9e',
}));

const CurrencyFormatter = ({ currency, onCurrencyChanged, isLoading }: CurrencyFormatterProps) => {
  const foreignCurrency = currency.sourceCurrency !== currency.code;
  const { isExportMode } = useLayoutContext();
  const {
    usedCurrencies, currencies, showMore, showLess
  } = useCurrencies();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCurrencyChange = (selectedCurrency: Currency) => () => {
    if (!isLoading) {
      onCurrencyChanged(selectedCurrency);
      handleClose();
    }
  };

  const roundedExchangeRate = useMemo(
    () => (currency.exchangeRate ? Math.round(currency.exchangeRate * 100) / 100 : undefined),
    [currency.exchangeRate]
  );

  const getCurrencyMessage = useCallback(
    ({ sourceCurrency, exchangeRate, currencyName }) => {
      const message = `1 ${sourceCurrency} -> ${exchangeRate} ${currency.code}`;
      if (currencyName) {
        return `${currencyName}: ${message}`;
      }
      return message;
    },
    [currency]
  );

  return (
    <>
      <Tooltip
        title={
          <div>
            <div>{getCurrencyMessage(currency)}</div>
            {currency.additionalCurrencies?.map(additionalCurrency => (
              <div key={additionalCurrency.sourceCurrency as string}>{getCurrencyMessage(additionalCurrency)}</div>
            ))}
          </div>
        }
        arrow>
        <span>
          <StyledCurrencyButton
            foreignCurrency={foreignCurrency}
            isExportMode={isExportMode}
            onClick={handleClick}
            disabled={isLoading}>
            <Grid container alignItems="center">
              <Grid>
                {foreignCurrency ? '1 ' : ''}
                {currency.sourceCurrency}
              </Grid>
              {foreignCurrency && (
                <>
                  <Grid>
                    <StyledIcon />
                  </Grid>
                  <Grid>
                    {roundedExchangeRate} {currency.code}
                  </Grid>
                </>
              )}
            </Grid>
          </StyledCurrencyButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {currencies.map(c => {
          const isLastUsedCurrency = c.code === usedCurrencies[usedCurrencies.length - 1];
          const MenuItemComponent = isLastUsedCurrency ? StyledMenuSeparator : MenuItem;

          return (
            <MenuItemComponent key={c.code} onClick={handleCurrencyChange(c)}>
              {`${c.code} (${c.symbol})`}
            </MenuItemComponent>
          );
        })}
        <StyledShowMoreLess onClick={currencies.length === usedCurrencies.length ? showMore : showLess}>
          {currencies.length === usedCurrencies.length ? SHOW_MORE : SHOW_LESS}
        </StyledShowMoreLess>
      </Menu>
    </>
  );
};

export default CurrencyFormatter;
